import React from 'react';
import { Box, Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import Image from 'next/image';
import { useI18nContext } from '@procore/core-react';
import { Business } from '../../types/business';
import { useStyles } from './BusinessProfileAbout.styles';
import {
  buildAvgContractSizeText,
  buildCompanySizeText,
  buildJoinedProcoreInText,
} from '../../helpers/build-texts';
import ExpandableText from '../Layout/ExpandableText';
import DescriptionWithIcon from './DescriptionWithIcon';
import ImageLoader from '../utils/ImageLoader';

const Icon: React.FC<{
  src: string;
  alt: string;
  width;
  height;
}> = ({ src, alt, width, height }) => {
  return (
    <Image
      loader={ImageLoader}
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading="eager"
    />
  );
};

const BusinessProfileAbout = ({ business }: { business: Business }) => {
  const I18n = useI18nContext();
  const classes = useStyles();
  const separator = ', ';
  const companySizeText = business.companySize
    ? buildCompanySizeText(business, I18n)
    : '';
  const avgContractSizeText = business.avgContractSize
    ? buildAvgContractSizeText(business, I18n)
    : '';
  const legalNameText = business.dba ? business.name : '';
  const joinedProcoredInText = business.monolithCreatedAt
    ? buildJoinedProcoreInText(business, I18n)
    : '';
  const providedServicesCount = business?.providedServices?.length || 0;
  const constructionSectorsCount = business?.constructionSectors?.length || 0;
  const businessTypesCount = business?.businessTypes?.length || 0;
  const classificationsCount = business.classifications?.length || 0;

  return (
    <Box
      data-test-id="business-profile-nav-about-contents"
      style={{ paddingBottom: '17px' }}
    >
      {companySizeText || avgContractSizeText || joinedProcoredInText ? (
        <Box
          style={{
            columnWidth: '600px',
            paddingTop: '24px',
          }}
          data-test-id="business-profile-nav-about-box-info"
        >
          {companySizeText && (
            <DescriptionWithIcon
              icon={
                <Icon
                  src="/business-profile/people-group-icon.png"
                  alt=""
                  width="21"
                  height="16"
                />
              }
              descriptionWithIconStyle={classes.descriptionWithIcon}
              textComponent={
                <Typography
                  className={classes.descriptiveInfo}
                  data-test-id="business-profile-nav-about-company-size"
                >
                  {`${I18n.t(
                    'components.company_size.label'
                  )}: ${companySizeText}`}
                </Typography>
              }
            />
          )}
          {avgContractSizeText && (
            <DescriptionWithIcon
              icon={
                <Icon
                  src="/business-profile/vector-icon.png"
                  alt=""
                  width="20"
                  height="16"
                />
              }
              descriptionWithIconStyle={classes.descriptionWithIcon}
              textComponent={
                <Typography
                  className={classes.descriptiveInfo}
                  data-test-id="business-profile-nav-about-avg-contract-size"
                >
                  {`${I18n.t(
                    'components.avg_contract_size.label'
                  )}: ${avgContractSizeText}`}
                </Typography>
              }
            />
          )}
          {legalNameText && (
            <DescriptionWithIcon
              icon={
                <Icon
                  src="/business-profile/building-icon.png"
                  alt=""
                  width="22"
                  height="22"
                />
              }
              descriptionWithIconStyle={classes.descriptionWithIcon}
              textComponent={
                <Typography
                  className={classes.descriptiveInfo}
                  data-test-id="business-profile-nav-about-legal-name"
                >
                  {`${I18n.t(
                    'pages.p.business_profile_layout.legal_name'
                  )}: ${legalNameText}`}
                </Typography>
              }
            />
          )}
          {joinedProcoredInText && (
            <DescriptionWithIcon
              icon={
                <Icon src="/small-logo.webp" alt="" width="20" height="20" />
              }
              descriptionWithIconStyle={classes.descriptionWithIcon}
              textComponent={
                <Typography
                  className={classes.descriptiveInfo}
                  data-test-id="business-profile-nav-about-joined-procore"
                >
                  {joinedProcoredInText}
                </Typography>
              }
            />
          )}
        </Box>
      ) : null}
      <Grid
        container
        spacing={2}
        style={{ paddingTop: '24px', paddingBottom: '7px' }}
      >
        {providedServicesCount > 0 && (
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <Typography variant="h2" className={classes.sectionHeader}>
              {I18n.t(
                'pages.p.business_profile_layout.business_profile_header.trades_and_services'
              )}
            </Typography>
            <ExpandableText
              text={business.providedServices
                .map((item) => item.name)
                .sort((a, b) => a.localeCompare(b))
                .join(separator)}
              textClassName={classes.sectionText}
              expandTextClassName={classes.showMoreOrLessText}
              textCutoffLength={170}
            />
          </Grid>
        )}
        {constructionSectorsCount > 0 && (
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <Typography variant="h2" className={classes.sectionHeader}>
              {I18n.t(
                'pages.p.business_profile_layout.business_profile_nav_about.market_sectors'
              )}
            </Typography>
            <Typography
              className={classes.sectionText}
              data-test-id="business-profile-nav-about-market-sectors"
            >
              {business.constructionSectors
                .sort((a, b) => a.localeCompare(b))
                .join(separator)}
            </Typography>
          </Grid>
        )}
        {businessTypesCount > 0 && (
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <Typography variant="h2" className={classes.sectionHeader}>
              {I18n.t(
                'pages.p.business_profile_layout.business_profile_nav_about.company_types'
              )}
            </Typography>
            <Typography
              className={classes.sectionText}
              data-test-id="business-profile-nav-about-business-types"
            >
              {business.businessTypes
                .sort((a, b) => a.localeCompare(b))
                .join(separator)}
            </Typography>
          </Grid>
        )}
        {classificationsCount > 0 && (
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <Typography variant="h2" className={classes.sectionHeader}>
              {I18n.t(
                'pages.p.business_profile_layout.business_profile_nav_about.business_classifications'
              )}
            </Typography>
            <ExpandableText
              text={business.classifications
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((item) => {
                  return !item.abbreviation
                    ? item.name
                    : `${item.name} (${item.abbreviation})`;
                })
                .join('\n')}
              textClassName={classes.sectionText}
              expandTextClassName={classes.showMoreOrLessText}
              textCutoffLength={
                business.classifications
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((item) => {
                    return !item.abbreviation
                      ? item.name
                      : `${item.name} (${item.abbreviation})`;
                  })
                  .slice(0, 4)
                  .join('\n').length
              }
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default BusinessProfileAbout;
